import { Box, Typography, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import { useTerritorySalesMetrics } from "../../hooks/useTerritorySalesMetrics";
import { format2 } from "../../helpers/helper";

export default function SalesAchievedTerritoryWise({
    selectedTerritory,
    dateParams,
    isDateRangeIncomplete,
}) {
    const year = dateParams?.year || new Date().getFullYear();
    const territoryId = String(selectedTerritory);
    const { data, isLoading, error } = useTerritorySalesMetrics({ territoryId, dateParams, year, isDateRangeIncomplete });
    const hasData =
        !!data &&
        (
            data.budgetRetail > 0 ||
            data.budgetProject > 0 ||
            data.salesRetail > 0 ||
            data.salesProject > 0
        );

    if (isLoading) {
        return (
            <Box
                sx={{
                    minHeight: 294,
                    p: 2,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 4,
                }}
            >
                <Skeleton variant="circular" width={140} height={140} />
                <Skeleton variant="circular" width={140} height={140} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    minHeight: 294,
                    p: 2,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    if (isDateRangeIncomplete) {
        return (
            <Box
                sx={{
                    minHeight: 294,
                    p: 2,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "warning.main", fontSize: 12 }}>
                    Select both From & To dates
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: 294,
                p: { xs: 2.5, lg: 2 },
                borderRadius: "20px",
                bgcolor: "background.paper",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.2s ease",
                "&:hover": { transform: "translateY(-2px)" },
            }}
        >
            <Typography sx={{ fontSize: { xs: 12, lg: 14 }, color: "secondary.main", mb: 2 }}>
                Sales Achieved Against Budget – Territory wise
            </Typography>

            {!hasData ? (
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        No sales / budget data available
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        rowGap: 1,
                    }}
                >
                    <Donut
                        value={data?.retailAchieved}
                        label="RETAIL"
                        color="#22C4BC"
                    />
                    <Donut
                        value={data?.projectAchieved}
                        label="PROJECT"
                        color="#6B7CFF"
                    />
                </Box>
            )}
        </Box>
    );
}

const Donut = ({ value, label, color }) => {
    const theme = useTheme();
    const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
    const hasValue = typeof value === "number";
    const hasPositiveValue = hasValue && value > 0;
    const safeValue = hasValue ? Math.min(Math.max(value, 0), 100) : 0;
    const size = isSmDown ? 110 : 140;
    const radius = isSmDown ? 48 : 60;
    const stroke = isSmDown ? 12 : 14;
    const center = size / 2;
    const circumference = 2 * Math.PI * radius;
    const arcPercent = 0.75;
    const arcLength = circumference * arcPercent;
    const offset = arcLength - (safeValue / 100) * arcLength;

    return (
        <Box sx={{ textAlign: "center" }}>
            <svg width={size} height={size}>
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    stroke={theme.palette.divider}
                    strokeWidth={stroke}
                    fill="none"
                    strokeDasharray={`${arcLength} ${circumference}`}
                    transform={`rotate(135 ${center} ${center})`}
                />

                {hasPositiveValue && (
                    <circle
                        cx={center}
                        cy={center}
                        r={radius}
                        stroke={color}
                        strokeWidth={stroke}
                        fill="none"
                        strokeDasharray={`${arcLength} ${circumference}`}
                        strokeDashoffset={offset}
                        transform={`rotate(135 ${center} ${center})`}
                        style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
                    />
                )}

                <text
                    x={center}
                    y={center + 6}
                    textAnchor="middle"
                    fontSize={isSmDown ? 14 : 18}
                    fill={theme.palette.text.primary}
                >
                    {hasValue ? `${format2(value)} %` : "--"}
                </text>
            </svg>

            <Typography
                sx={{
                    fontSize: 12,
                    mt: "-8px",
                    letterSpacing: "0.12em",
                    opacity: 0.8,
                    color: "text.primary",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
};